import { tokens } from "eth/contracts/contracts";
import { Contract } from "ethers";
import { runInAction } from "mobx";
import PriceStore from "stores/PriceStore";
import getPricingFor from "./coingecko";
import { formatResult } from "./utils";

/**
 *
 * @param token the token we are looking the price for
 * @param provider web3 provider
 * @param uniToken the uni LP token
 * @param pairToken ther pair token in the UNI we can search a price for, for example in DEFLCT/RFI it would be RFI.
 */
export async function getPriceFor(token, provider, uniToken, pairToken) {
  const price = PriceStore.prices.get(token.name);
  if (!PriceStore.canFetch() && price) {
    return price;
  }
  const res = await getPricingFor(token.address, false);
  if (res && res.usd) {
    return parseFloat(res.usd);
  }
  if (!token.isLPtoken) {
    let price = await getPriceAgainst(token, pairToken, uniToken, provider);
    if (!price) {
      price = 0;
    }
    runInAction(() => PriceStore.prices.set(token.name, price));
    return price;
  }

  const STAKING_TOKEN = new Contract(token.address, token.abi, provider);

  const PAIR_TOKEN = new Contract(pairToken.address, pairToken.abi, provider);

  // Get amount of primary token in pair
  const PAIRBALANCE = formatResult(await PAIR_TOKEN.balanceOf(uniToken.address), token.decimals);
  //Get number of secondary token in pair
  const TOKENBALANCE = formatResult(await STAKING_TOKEN.balanceOf(uniToken.address), token.decimals);

  // Price of the opposite pair
  const PAIRPRICE = await getPricingFor(pairToken.address, "USD");
  let TOKENPRICE = (PAIRBALANCE * PAIRPRICE.usd) / TOKENBALANCE;
  if (!TOKENPRICE || !isFinite(TOKENPRICE)) {
    TOKENPRICE = 0;
  }
  return TOKENPRICE;
}

// Get price against token
export async function getPriceAgainst(token, againstToken, uniToken, provider) {
  const price = PriceStore.prices.get(token.name);
  if (!PriceStore.canFetch() && price) return price;
  if (!provider) return 0;
  // If againstToken is not found use WETH.
  if (!againstToken) {
    againstToken = tokens.homestead.find((token) => token.name === "WETH");
  }
  // Initialize contract for the against token
  const AGAINST_TOKEN = new Contract(againstToken.address, againstToken.abi, provider);

  // Initialize contract for the target token
  const TOKEN_TO_GET_PRICE_FOR = new Contract(token.address, token.abi, provider);

  // Get amount of against token in pool. The UNI token itself holds the number.
  const AGAINST_TOKEN_BALANCE = formatResult(await AGAINST_TOKEN.balanceOf(uniToken.address), againstToken.decimals);

  // Get target token balance. The UNI token itself holds the number.
  const TOKEN_TO_GET_PRICE_FOR_BALANCE = formatResult(await TOKEN_TO_GET_PRICE_FOR.balanceOf(uniToken.address), token.decimals);

  // Get the against token price.
  let AGAINST_TOKEN_PRICE = PriceStore.prices.get(againstToken.name);
  if (!AGAINST_TOKEN_PRICE) {
    const result = await getPricingFor(AGAINST_TOKEN.address, false);
    AGAINST_TOKEN_PRICE = result.usd;
  }

  // Get the target price.
  const TARGETPRICE = (AGAINST_TOKEN_BALANCE * AGAINST_TOKEN_PRICE!) / TOKEN_TO_GET_PRICE_FOR_BALANCE;

  runInAction(() => PriceStore.prices.set(token.name, TARGETPRICE));

  return TARGETPRICE;
}

export async function getUNIPrice(uniToken, pair0, provider) {
  const priceInStore = PriceStore.prices.get(uniToken.name);
  if (!PriceStore.canFetch() && priceInStore) {
    return priceInStore;
  }
  /* MAIN */
  const UNI_POOL = new Contract(uniToken.address, uniToken.abi, provider);

  const UNI_PAIR_TOKEN = new Contract(pair0.address, pair0.abi, provider);

  const TOTAL_PAIR_IN_UNI_POOL = formatResult(await UNI_PAIR_TOKEN.balanceOf(uniToken.address), pair0.decimals);

  const TOTAL_PAIR_SUPPLY = formatResult(await UNI_POOL.totalSupply(), 18);

  console.log(TOTAL_PAIR_SUPPLY, TOTAL_PAIR_IN_UNI_POOL);

  let UNI_PAIR_PRICE = PriceStore.prices.get(pair0.name);
  if (!UNI_PAIR_PRICE) {
    // Make  a query if not
    const results = await getPricingFor(pair0.address, false);
    runInAction(() => PriceStore.prices.set(pair0.name, results.usd));
    UNI_PAIR_PRICE = results.usd;
  }

  const TOTAL_POOL_VALUE = TOTAL_PAIR_IN_UNI_POOL * UNI_PAIR_PRICE! * 2;
  const UNIPRICE = TOTAL_POOL_VALUE / TOTAL_PAIR_SUPPLY;

  runInAction(() => PriceStore.prices.set(uniToken.name, UNIPRICE));
  return UNIPRICE;
}

/**
 *  Gets yield for LP input pool.
 */
export async function getYieldLP(pool, rewardRate, rewardPerToken, provider) {
  // Initiate pool
  const POOL = new Contract(pool.address, pool.abi, provider);

  // Staking token address (LP TOKENS)
  const stakingToken = new Contract(pool.stakingToken.address, pool.stakingToken.abi, provider);

  // Weekly reward for the pool
  const totalStakedUNI = formatResult(await stakingToken.balanceOf(POOL.address), pool.stakingToken.decimals);
  const weeklyReward = await getUniPoolWeeklyRewards(rewardRate, pool.rewardToken.decimals);
  const rewardPerTokens = weeklyReward / totalStakedUNI;
  const { REWARD_TOKEN_PRICE, STAKING_TOKEN_PRICE } = await getStakingAndRewardTokenPrices(pool, provider);
  const weeklyROI = ((rewardPerTokens * REWARD_TOKEN_PRICE * 100) / STAKING_TOKEN_PRICE).toFixed(2);

  return {
    rewardPerTokens,
    dailyROI: (parseFloat(weeklyROI) / 7).toFixed(2),
    weeklyROI,
    yearlyROI: (parseFloat(weeklyROI) * 52).toFixed(2),
  };
}

/**
 * Uni pool reward rate calculation
 */
async function getUniPoolWeeklyRewards(rewardRate, rewardTokenDecimals) {
  return Math.round((rewardRate / 10 ** rewardTokenDecimals) * 604800);
}

export async function getStakingAndRewardTokenPrices(pool, provider) {
  let STAKING_TOKEN_PRICE = PriceStore.prices.get(pool.stakingToken.name);
  if (!STAKING_TOKEN_PRICE) {
    STAKING_TOKEN_PRICE = await getPriceFor(pool.stakingToken, provider, pool.uniToken, pool.againstToken);
    runInAction(() => PriceStore.prices.set(pool.stakingToken.name, STAKING_TOKEN_PRICE!));
  }

  let REWARD_TOKEN_PRICE = PriceStore.prices.get(pool.rewardToken.name);
  if (!REWARD_TOKEN_PRICE) {
    REWARD_TOKEN_PRICE = await getPriceFor(pool.rewardToken, provider, pool.uniToken, tokens.WETH);
    runInAction(() => PriceStore.prices.set(pool.rewardToken.name, REWARD_TOKEN_PRICE!));
  }

  return {
    REWARD_TOKEN_PRICE,
    STAKING_TOKEN_PRICE,
  };
}
